import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  Star, 
  AlertCircle, 
  Lightbulb, 
  Download, 
  Share2, 
  ArrowRight,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  RotateCcw,
  Compass,
  Loader2,
  Mail
} from 'lucide-react';
import { weddingProfiles, generatePriorityMap, generateAlerts } from '../../data/simulatorData';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import EmailResultsModal from '../modals/EmailResultsModal';

const SimulatorResults = ({ result, answers, onRestart, onContact }) => {
  const navigate = useNavigate();
  const resultsRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { profile, secondaryProfile, percentages, primary, secondary } = result;
  
  const priorities = generatePriorityMap(answers, profile);
  const alerts = generateAlerts(answers, result);

  const handleExportPDF = async () => {
    if (!resultsRef.current || isExporting) return;
    
    setIsExporting(true);
    
    try {
      // Create a clone of the results content for PDF generation
      const element = resultsRef.current;
      
      // Generate canvas from the HTML content
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#FAF9F7',
        logging: false,
        windowWidth: 1200,
        windowHeight: element.scrollHeight
      });
      
      // Calculate dimensions for A4
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      let heightLeft = imgHeight;
      let position = 0;
      
      // Add first page
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if content is longer
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Save the PDF
      pdf.save(`Perfil-Casamento-${profile.name.replace(/\s+/g, '-')}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Fallback to print
      window.print();
    } finally {
      setIsExporting(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'O Meu Perfil de Casamento - Alma Leve Weddings',
          text: `Descobri que o meu perfil de casamento é "${profile.name}"! Descobre o teu em:`,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copiado para a área de transferência!');
    }
  };

  return (
    <div className="min-h-screen bg-transparent" ref={resultsRef}>
      {/* Hero Section */}
      <div className="relative py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full text-sm text-[#8B7D6B] mb-8 shadow-sm">
            <Star size={16} className="text-[#C4B5A0]" />
            O vosso perfil de casamento
          </div>

          {/* Profile Name */}
          <div 
            className="inline-block px-6 py-3 rounded-2xl mb-6"
            style={{ backgroundColor: `${profile.color}30` }}
          >
            <span className="text-4xl mr-3">{profile.emoji}</span>
            <span 
              className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl"
              style={{ color: '#2C2C2C' }}
            >
              {profile.name}
            </span>
          </div>

          {/* Description */}
          <p className="text-xl md:text-2xl text-[#6B4E37] max-w-2xl mx-auto leading-relaxed mb-8">
            {profile.description}
          </p>

          {/* Secondary Profile */}
          <p className="text-[#8B7D6B]">
            Com influências de <span className="font-medium text-[#6B4E37]">{secondaryProfile.name}</span> ({percentages[secondary]}%)
          </p>
        </div>
      </div>

      {/* Profile Breakdown */}
      <div className="py-12 px-6 bg-white/60 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C] mb-8 text-center">
            A vossa composição emocional
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(percentages).map(([key, value]) => (
              <div 
                key={key}
                className="bg-white rounded-xl p-4 text-center shadow-sm"
              >
                <div className="text-2xl mb-2">{weddingProfiles[key].emoji}</div>
                <div 
                  className="text-3xl font-light mb-1"
                  style={{ color: weddingProfiles[key].color }}
                >
                  {value}%
                </div>
                <div className="text-xs text-[#8B7D6B] uppercase tracking-wider">
                  {weddingProfiles[key].name.split(' ')[0]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Strengths & Watch Outs */}
      <div className="py-16 px-6">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          {/* Strengths */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#B5C4B1]/30 flex items-center justify-center">
                <TrendingUp size={20} className="text-[#6B4E37]" />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C]">
                Os vossos pontos fortes
              </h3>
            </div>
            <ul className="space-y-4">
              {profile.strengths.map((strength, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-[#B5C4B1] flex-shrink-0 mt-1" />
                  <span className="text-[#6B4E37]">{strength}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Watch Outs */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#E8D5D5]/50 flex items-center justify-center">
                <TrendingDown size={20} className="text-[#6B4E37]" />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C]">
                Pontos de atenção
              </h3>
            </div>
            <ul className="space-y-4">
              {profile.watchOuts.map((watchOut, index) => (
                <li key={index} className="flex items-start gap-3">
                  <AlertCircle size={18} className="text-[#D4C4B0] flex-shrink-0 mt-1" />
                  <span className="text-[#6B4E37]">{watchOut}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Alerts & Recommendations */}
      {alerts.length > 0 && (
        <div className="py-12 px-6 bg-white/60 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C] mb-8 text-center">
              Alertas personalizados para vocês
            </h3>
            <div className="space-y-4">
              {alerts.map((alert, index) => (
                <div 
                  key={index}
                  className={`rounded-xl p-6 ${
                    alert.type === 'warning' 
                      ? 'bg-[#E8D5D5]/30 border border-[#E8D5D5]' 
                      : alert.type === 'attention'
                      ? 'bg-[#D4C4B0]/20 border border-[#D4C4B0]'
                      : 'bg-[#B5C4B1]/20 border border-[#B5C4B1]'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      alert.type === 'warning' 
                        ? 'bg-[#E8D5D5]' 
                        : alert.type === 'attention'
                        ? 'bg-[#D4C4B0]'
                        : 'bg-[#B5C4B1]'
                    }`}>
                      {alert.type === 'tip' ? (
                        <Lightbulb size={20} className="text-white" />
                      ) : (
                        <AlertCircle size={20} className="text-white" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-[#2C2C2C] mb-1">{alert.title}</h4>
                      <p className="text-[#6B4E37]">{alert.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recommendations */}
      <div className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C] mb-8 text-center">
            Recomendações para o vosso casamento
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {profile.recommendations.map((rec, index) => (
              <div 
                key={index}
                className="bg-white/80 backdrop-blur-sm rounded-xl p-5 shadow-sm flex items-start gap-4"
              >
                <div className="w-8 h-8 rounded-full bg-[#C4B5A0]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#6B4E37] font-medium text-sm">{index + 1}</span>
                </div>
                <p className="text-[#6B4E37]">{rec}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Budget Focus */}
      <div className="py-12 px-6 bg-white/60 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C] mb-8 text-center">
            Sugestão de distribuição de orçamento
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {/* High Priority */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-[#B5C4B1]" />
                <span className="text-sm font-medium text-[#2C2C2C] uppercase tracking-wider">
                  Investir mais
                </span>
              </div>
              <ul className="space-y-2">
                {profile.budgetFocus.high.map((item, index) => (
                  <li key={index} className="text-[#6B4E37] text-sm flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#B5C4B1]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Medium Priority */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-[#D4C4B0]" />
                <span className="text-sm font-medium text-[#2C2C2C] uppercase tracking-wider">
                  Investimento médio
                </span>
              </div>
              <ul className="space-y-2">
                {profile.budgetFocus.medium.map((item, index) => (
                  <li key={index} className="text-[#6B4E37] text-sm flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#D4C4B0]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Low Priority */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-[#E8E4E0]" />
                <span className="text-sm font-medium text-[#2C2C2C] uppercase tracking-wider">
                  Onde poupar
                </span>
              </div>
              <ul className="space-y-2">
                {profile.budgetFocus.low.map((item, index) => (
                  <li key={index} className="text-[#6B4E37] text-sm flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#E8E4E0]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Tools CTA - New Section */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-12 text-center shadow-sm">
            <div className="w-14 h-14 rounded-xl bg-[#B5C4B1]/30 flex items-center justify-center mx-auto mb-4">
              <Compass size={28} className="text-[#6B4E37]" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C] mb-3">
              Explorem as Ferramentas de Planeamento
            </h3>
            <p className="text-[#6B4E37] mb-6 max-w-lg mx-auto">
              7 ferramentas estratégicas criadas para vos ajudar a tomar decisões com clareza: 
              desde o radar de decisões difíceis até ao mapa de concessões do casal.
            </p>
            <button
              onClick={() => navigate('/ferramentas')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#B5C4B1] text-white rounded-lg font-medium hover:bg-[#6B4E37] transition-all duration-300 shadow-md"
              data-testid="explore-tools-btn"
            >
              Explorar Ferramentas
              <ArrowRight size={18} />
            </button>
          </div>

          {/* Export & Share */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-lg text-[#6B4E37] hover:bg-white transition-all duration-300 shadow-sm disabled:opacity-50"
              data-testid="export-pdf-btn"
            >
              {isExporting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  A gerar PDF...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Exportar PDF
                </>
              )}
            </button>
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-lg text-[#6B4E37] hover:bg-white transition-all duration-300 shadow-sm"
              data-testid="email-results-btn"
            >
              <Mail size={18} />
              Enviar por Email
            </button>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-lg text-[#6B4E37] hover:bg-white transition-all duration-300 shadow-sm"
              data-testid="share-btn"
            >
              <Share2 size={18} />
              Partilhar
            </button>
            <button
              onClick={onRestart}
              className="flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-lg text-[#8B7D6B] hover:bg-white transition-all duration-300 shadow-sm"
              data-testid="restart-btn"
            >
              <RotateCcw size={18} />
              Refazer
            </button>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-[#D4C4B0]/30 to-[#B5C4B1]/30 rounded-2xl p-8 md:p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-white/80 flex items-center justify-center mx-auto mb-6 shadow-sm">
              <Heart size={28} className="text-[#6B4E37]" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-3xl text-[#2C2C2C] mb-4">
              Prontos para dar o próximo passo?
            </h3>
            <p className="text-[#6B4E37] mb-8 max-w-xl mx-auto">
              Agora que conhecem o vosso perfil, podemos começar a planear um casamento verdadeiramente vosso. Vamos conversar?
            </p>
            <button
              onClick={onContact}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C4B5A0] text-white rounded-lg font-medium hover:bg-[#6B4E37] transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Falar com a Véronique
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Email Results Modal */}
      <EmailResultsModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        profileData={result}
      />
    </div>
  );
};

export default SimulatorResults;
